import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, FileStack, Loader2, Search } from "lucide-react";
import { KbGenerationTemplateDetailSheet } from "../components/organisms/KbGenerationTemplateDetailSheet";
import { buildFieldSteps } from "../services/gameRuleKbGenerateService";
import {
  getKbGenerationTemplate,
  listKbGenerationTemplates,
} from "../services/gameRuleKbGenerationTemplateService";
import type {
  KbGenerationTemplateDetail,
  KbGenerationTemplateListItem,
} from "../types/gameRuleKbGenerationTemplate";
import { formatKbGenerationTemplateRowMeta } from "../utils/kbGenerationTemplateDisplay";

const PAGE_SIZE = 20;

export const GameRuleGenerateTemplatesPage = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<readonly KbGenerationTemplateListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] =
    useState<KbGenerationTemplateDetail | null>(null);
  const [sectionNames, setSectionNames] = useState<readonly string[]>([]);

  const hasMore = items.length < total;
  const rangeStart = items.length === 0 ? 0 : 1;
  const rangeEnd = items.length;

  const loadPage = useCallback(
    async (nextOffset: number, append: boolean) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const result = await listKbGenerationTemplates(
          appliedSearch.trim() || undefined,
          { limit: PAGE_SIZE, offset: nextOffset },
        );
        setTotal(result.total);
        setOffset(nextOffset + result.items.length);
        setItems((prev) =>
          append ? [...prev, ...result.items] : result.items,
        );
      } catch (err: unknown) {
        setError(
          err instanceof Error ? err.message : "Unable to load templates.",
        );
        if (!append) {
          setItems([]);
          setTotal(0);
        }
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [appliedSearch],
  );

  useEffect(() => {
    setOffset(0);
    void loadPage(0, false);
  }, [loadPage]);

  const openTemplateSheet = useCallback((templateId: string) => {
    setSheetOpen(true);
    setSheetLoading(true);
    setSheetError(null);
    setSelectedTemplate(null);
    setSectionNames([]);

    void (async () => {
      try {
        const detail = await getKbGenerationTemplate(templateId);
        setSelectedTemplate(detail);
        if (detail.sectionIds.length === 0) {
          setSectionNames([]);
          return;
        }
        const steps = await buildFieldSteps(detail.sectionIds);
        setSectionNames(steps.map((step) => step.name));
      } catch (err: unknown) {
        setSheetError(
          err instanceof Error
            ? err.message
            : "Unable to load template details.",
        );
      } finally {
        setSheetLoading(false);
      }
    })();
  }, []);

  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    setSheetLoading(false);
    setSheetError(null);
    setSelectedTemplate(null);
    setSectionNames([]);
  }, []);

  const useSelectedTemplate = useCallback(() => {
    if (!selectedTemplate) {
      return;
    }
    navigate("/prizelogic/generate-document/wizard", {
      state: { fromTemplateId: selectedTemplate.id },
    });
  }, [navigate, selectedTemplate]);

  const listBusy = loading || loadingMore;

  return (
    <div className="relative space-y-4">
      <KbGenerationTemplateDetailSheet
        open={sheetOpen}
        template={selectedTemplate}
        sectionNames={sectionNames}
        loading={sheetLoading}
        error={sheetError}
        onClose={closeSheet}
        onUseTemplate={useSelectedTemplate}
      />

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Choose a template</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Reuse a saved section package. Section selection is skipped in the
            wizard.
          </p>
        </div>
        <Link
          to="/prizelogic/generate-document"
          className="shrink-0 text-sm font-medium text-primary hover:underline"
        >
          Back
        </Link>
      </div>

      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => void loadPage(0, false)}
          >
            Retry
          </button>
        </div>
      ) : null}

      <div className="flex flex-wrap items-stretch gap-2">
        <div className="relative min-w-0 max-w-md flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            placeholder="Search templates by name…"
            disabled={listBusy}
            className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-3 text-sm disabled:opacity-50"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                if (!listBusy) {
                  setAppliedSearch(searchInput);
                }
              }
            }}
          />
        </div>
        <button
          type="button"
          disabled={listBusy}
          className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-primary hover:bg-accent disabled:opacity-50"
          onClick={() => setAppliedSearch(searchInput)}
        >
          Search
        </button>
      </div>

      {!loading && total > 0 ? (
        <p className="text-[11px] text-muted-foreground">
          Showing {rangeStart}–{rangeEnd} of {total}
        </p>
      ) : null}

      <div className="overflow-hidden rounded-lg border border-[var(--border)] bg-card divide-y divide-border">
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <div className="space-y-2 px-4 py-8 text-center text-sm text-muted-foreground">
            <FileStack
              className="mx-auto h-8 w-8 text-muted-foreground/60"
              aria-hidden
            />
            <p>
              {appliedSearch.trim()
                ? `No templates match "${appliedSearch.trim()}".`
                : "No saved templates yet. Complete a generate flow and save as template on Step 5."}
            </p>
            {!appliedSearch.trim() ? (
              <Link
                to="/prizelogic/generate-document"
                className="inline-block text-sm font-medium text-primary hover:underline"
              >
                Back to Generate Document
              </Link>
            ) : null}
          </div>
        ) : (
          items.map((template) => (
            <button
              key={template.id}
              type="button"
              className="flex w-full items-center gap-3 px-3 py-3 text-left hover:bg-muted/40"
              onClick={() => openTemplateSheet(template.id)}
            >
              <FileStack
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-xs font-bold text-primary">
                  {template.name}
                </span>
                <span className="block truncate text-[11px] text-muted-foreground">
                  {formatKbGenerationTemplateRowMeta(template)}
                </span>
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground"
                aria-hidden
              />
            </button>
          ))
        )}
      </div>

      {hasMore && !loading ? (
        <button
          type="button"
          disabled={loadingMore}
          className="w-full rounded-lg border border-input bg-background px-4 py-2.5 text-sm font-medium text-primary hover:bg-accent disabled:opacity-50"
          onClick={() => void loadPage(offset, true)}
        >
          {loadingMore ? (
            <span className="inline-flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading…
            </span>
          ) : (
            "Load more"
          )}
        </button>
      ) : null}
    </div>
  );
};
